import { json, type MetaFunction, redirect } from '@remix-run/node'
import { Form, Link, useActionData } from '@remix-run/react'
import { type ActionFunctionArgs } from '@remix-run/router'

import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { CheckboxField, ErrorList, Field, OTPField, PasswordField } from '#app/components/forms/forms.tsx'
import { Button } from '#app/components/forms/button.tsx'
import { StatusButton } from '#app/components/forms/status-button.tsx'

import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { convertOnToBoolean, useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema, PasswordSchema, PhoneSchema } from '#app/utils/user-validation.ts'
import { useState } from 'react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { post, postAndReturnStatus } from '#app/utils/refundr.server.aragorn.ts'
import { type DashboardLoginResponse, type SignUpInput, type SignUpResponse } from '#app/model/model.ts'
import ExternalLink from '#app/components/buttons/external-link.tsx'
import {
	getSessionExpirationDate,
	sessionAuthProviderKey,
	sessionKey,
	sessionStorage,
} from '#app/utils/session.server.ts'

const SignupSchema = z.object({
	email: EmailSchema,
	phone: PhoneSchema,
	password: PasswordSchema,
	agreeToTermsOfServiceAndPrivacyPolicy: z.boolean({
		required_error: 'You must agree to the terms of service and privacy policy',
	}),
	redirectTo: z.string().optional(),
})

const VerifySchema = z.object({
	code: z.string({ required_error: 'Code is required.' }).min(6).max(6),
	email: EmailSchema,
	password: PasswordSchema,
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()

	checkHoneypot(formData)

	const intent = formData.get('intent')
	if (intent === 'resend') {
		return resendCode(request, formData)
	}
	if (intent === 'verify') {
		return verifyAndLogin(request, formData)
	}

	// form entries -> object
	const payload = convertOnToBoolean(Object.fromEntries(formData))

    // parse with zod
    const result = SignupSchema.safeParse(payload)

    // respond with zod errors
    if (!result.success) {
        const error = result.error.flatten()
        return {
            payload,
            formErrors: error.formErrors,
            fieldErrors: error.fieldErrors,
        }
    }

    const response = await post<SignUpInput, SignUpResponse>(request, '/aws/signup/', {
        email: result.data.email,
        password: result.data.password,
        phone: result.data.phone,
    }, true)

    if (!response.success) {
        return {
            payload,
            formErrors: [typeof response.data === 'string' ? response.data : 'We could not create your account.'],
            fieldErrors: {},
        }
    }

	return verifyResult(result.data.email)
}

function verifyResult(email: string, extra?: { formErrors?: string[]; notice?: string }) {
	return json({
		step: 'verify' as const,
		email,
		formErrors: extra?.formErrors,
		notice: extra?.notice,
	})
}

async function resendCode(request: Request, formData: FormData) {
	const email = String(formData.get('email') ?? '')
	const response = await postAndReturnStatus(request, '/aws/resend-code/', { email }, true)
	if (response.success) {
		return verifyResult(email, { notice: 'We have sent you another code. Check your email.' })
	}
	return verifyResult(email, {
		formErrors: ['We were unable to send another confirmation code. Check your email and try again.'],
	})
}

async function verifyAndLogin(request: Request, formData: FormData) {
	const email = String(formData.get('email') ?? '')
	const parsed = VerifySchema.safeParse({
		code: formData.get('code'),
		email,
		password: formData.get('password'),
	})
	if (!parsed.success) {
		return verifyResult(email, { formErrors: ['Enter the 6-digit code from your email.'] })
	}

	const confirmation = await postAndReturnStatus<{ code: string; email: string }>(
		request,
		'/aws/validate/',
		{ code: parsed.data.code, email: parsed.data.email },
		true,
	)
	if (!confirmation.success) {
		return verifyResult(parsed.data.email, { formErrors: ['Invalid code.'] })
	}

	const response = await post<{ email: string; password: string }, DashboardLoginResponse>(
		request,
		'/aws/login/',
		{
			email: parsed.data.email,
			password: parsed.data.password,
		},
		true,
	)

	if (!response.success) {
		const message = typeof response.data === 'string' && response.data ? response.data : 'Please check your credentials and try again.'
		return verifyResult(parsed.data.email, { formErrors: [message] })
	}

	if ('NEW_PASSWORD_REQUIRED' === response.data?.mfaType) {
		return redirect(`/force-change-password?email=${encodeURIComponent(parsed.data.email)}`)
	}

	if ('MFA_SETUP' === response.data?.mfaType) {
		const params = new URLSearchParams({ email: parsed.data.email })
		return redirect(`/mfa-setup?${params.toString()}`)
	}

	if ('SOFTWARE_TOKEN_MFA' === response.data?.mfaType) {
		const params = new URLSearchParams({
			email: parsed.data.email,
			session: response.data.thirdPartySessionId,
		})
		return redirect(`/mfa-challenge?${params.toString()}`)
	}

	if (response.data?.sessionId) {
		const authSession = await sessionStorage.getSession(request.headers.get('cookie'))
		authSession.set(sessionKey, response.data.sessionId)
		authSession.set(sessionAuthProviderKey, 'aws')
		return redirect('/dashboard', {
			headers: {
				'set-cookie': await sessionStorage.commitSession(authSession, {
					expires: getSessionExpirationDate(),
				}),
			},
		})
	}

	throw redirect('/500')
}

export const meta: MetaFunction = () => {
    return [{title: 'Create Account | refundr'}]
}

export default function SignupRoute() {
    const actionData = useActionData<typeof action>()
    const isPending = useIsPending()

    const [showPassword, setShowPassword] = useState(false)
    const [password, setPassword] = useState('')

    const [form, fields] = useForm({
        id: 'signup-form',
        constraint: getZodConstraint(SignupSchema),
        // @ts-ignore
        lastResult: actionData?.result,
        onValidate({formData}) {
            return parseWithZod(formData, {schema: SignupSchema})
        },
        shouldRevalidate: 'onBlur',
    })
    const signupFormProps = getFormProps(form)

    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev)
    }

    function generateStrongPassword(length = 12) {
        setShowPassword(false)
        const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
        const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz'
        const numbers = '0123456789'
        const specialChars = '!@#$%^&*()_+[]{}|;:,.<>?'
        const allChars = uppercaseChars + lowercaseChars + numbers + specialChars

        let generatedPassword = ''
        // Ensure at least one uppercase letter and one number
        generatedPassword += uppercaseChars.charAt(Math.floor(Math.random() * uppercaseChars.length))
        generatedPassword += numbers.charAt(Math.floor(Math.random() * numbers.length))

        // Fill the rest of the password length with random characters
        for (let i = 2; i < length; i++) {
            generatedPassword += allChars.charAt(Math.floor(Math.random() * allChars.length))
        }

        // Shuffle the password to randomize character positions
        const strongPassword = generatedPassword
            .split('')
            .sort(() => 0.5 - Math.random())
            .join('')

        const passwordElement = document.getElementById('signup-password')
        if (passwordElement) {
            // @ts-ignore
            passwordElement.value = strongPassword
            setPassword(strongPassword)
            setShowPassword(true)
        }
    }

    const isVerifyStep = actionData != null && 'step' in actionData && actionData.step === 'verify'
    const verifyEmail = isVerifyStep ? actionData.email : ''

    return (
        <div className="flex min-h-screen flex-col md:flex-row">
            {/* Left: Marketing Section */}
            <div className="dark-background order-last flex w-full items-center justify-center p-6 text-black md:order-none md:w-1/2">
                <div className="mt-5 flex flex-col items-center justify-center space-y-4">
                    <div>
                        <img className="block h-12 w-auto" src="/img/logo/refundr-white.png" alt="refundr" />
                    </div>
                    <div className="text-center font-mono text-gray-200">
                        Re-inventing how you connect with your Tax Clients.
                    </div>
                    <div className="pt-4">
                        <img className="block w-auto" src="/img/tax-return-card-md.png" alt="refundr"/>
                    </div>
                </div>
            </div>

            {/* Right: Login Section */}
            <div className="flex w-full items-center justify-center bg-white p-10 md:w-1/2 -mt-20">
                <div className="mt-5 w-full max-w-md bg-white mb-16">
                    <div className="mb-6 flex items-center justify-center">
                        <div className="text-2xl font-bold text-gray-600">Create Account for Tax Professionals</div>
                    </div>
									<div className="mb-6 flex flex-col gap-5 items-center justify-center rounded-lg bg-amber-300 p-6 text-center font-semibold text-black">
										<div>
											This site is for tax professionals only. If you are looking to have your taxes prepared, please visit
										</div>
										<div>
											<ExternalLink href="https://refundr.ca" className="h-12 w-full rounded-md bg-yellow-700 px-4 py-2 text-white hover:bg-yellow-900">
												refundr.ca
											</ExternalLink>
										</div>
									</div>
                    {isVerifyStep ? (
                        <div className="flex flex-col gap-4">
                            <div className="text-center">
                                <h1 className="text-2xl font-bold text-gray-600">Check your email</h1>
                                <p className="mt-3 text-sm text-gray-600">
                                    We've sent a code to verify your email address. Don't see it? Check your spam folder.
                                </p>
                            </div>
                            {actionData.notice ? <p className="text-center text-sm text-green-700">{actionData.notice}</p> : null}
                            <ErrorList errors={actionData.formErrors} />
                            <Form method="POST" className="flex flex-col items-center gap-3">
                                <HoneypotInputs />
                                <input type="hidden" name="intent" value="verify" />
                                <input type="hidden" name="email" value={verifyEmail} />
                                <input type="hidden" name="password" value={password} />
                                <OTPField
                                    className="flex flex-col items-center"
                                    slotClassName="h-16 w-14 text-3xl"
                                    labelProps={{
                                        htmlFor: 'signup-code',
                                        children: 'Code',
                                        className: 'mb-3 block text-center text-gray-700',
                                    }}
                                    inputProps={{
                                        name: 'code',
                                        id: 'signup-code',
                                        autoComplete: 'one-time-code',
                                        autoFocus: true,
                                        containerClassName: 'justify-center',
                                    }}
                                />
                                <StatusButton
                                    className="h-12 w-full rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                                    status={isPending ? 'pending' : 'idle'}
                                    type="submit"
                                    disabled={isPending || !password}
                                >
                                    Submit
                                </StatusButton>
                            </Form>
                            <Form method="POST">
                                <HoneypotInputs />
                                <input type="hidden" name="intent" value="resend" />
                                <input type="hidden" name="email" value={verifyEmail} />
                                <Button className="h-12 w-full rounded-md bg-blue-500 text-white hover:bg-blue-600" type="submit" disabled={isPending}>
                                    Resend code
                                </Button>
                            </Form>
                            <p className="text-center text-sm text-gray-600">
                                If you leave this page, <Link to="/login" className="text-blue-700 underline">log in</Link> with the password you just created.
                            </p>
                        </div>
                    ) : (
                    <Form method="POST" {...signupFormProps} onSubmit={event => {
                        const submittedPassword = String(new FormData(event.currentTarget).get(fields.password.name) ?? '')
                        if (submittedPassword) setPassword(submittedPassword)
                        signupFormProps.onSubmit?.(event)
                    }}>
                        <ErrorList errors={actionData?.formErrors} />

                        <HoneypotInputs/>
                        <div>
                            <Field
                                labelProps={{
                                    htmlFor: fields.email.id,
                                    children: 'email',
                                    className: 'text-gray-700',
                                }}
                                inputProps={{
                                    ...getInputProps(fields.email, {type: 'email'}),
                                    autoFocus: true,
                                    autoComplete: 'email',
                                    className: 'h-14 w-full rounded-lg bg-gray-100 px-3 py-2 text-black shadow border-gray-300',
                                    placeholder: 'enter your email',
                                }}
                                errors={fields.email.errors}
                            />
                        </div>
                        <div>
                            <PasswordField
                                createPassword={true}
                                generateStrongPassword={generateStrongPassword}
                                className="w-full"
                                showPassword={showPassword}
                                togglePasswordVisibility={togglePasswordVisibility}
                                inputProps={{
                                    ...getInputProps(fields.password, {
                                        type: 'password',
                                    }),
                                    autoComplete: 'new-password',
                                    placeholder: 'Enter your password',
                                    id: 'signup-password',
                                    onInput: event => setPassword(event.currentTarget.value),
                                }}
                                errors={fields.password.errors}
                            />
                        </div>
                        <div>
                            <Field
                                labelProps={{
                                    htmlFor: fields.phone.id,
                                    children: 'phone (example: 555-555-5555)',
                                    className: 'text-gray-700',
                                }}
                                inputProps={{
                                    ...getInputProps(fields.phone, {type: 'text'}),
                                    autoFocus: true,
                                    autoComplete: 'phone',
                                    className: 'lowercase h-14 w-full rounded-lg bg-gray-100 px-3 py-2 text-black shadow border-gray-300',
                                    placeholder: 'Enter your phone number',
                                }}
                                errors={fields.phone.errors}
                            />
                        </div>
                        <div>
                            <CheckboxField
                                checkBoxButtonClassName="border border-gray-400 bg-gray-100"
                                labelProps={{
                                    htmlFor: fields.agreeToTermsOfServiceAndPrivacyPolicy.id,
                                    children: <div>Do you agree to our <Link to="/terms" target="_blank"
                                                                             className="text-blue-500 hover:text-blue-700 underline">terms</Link> and <Link to="/privacy-policy"
                                                                                                                                                            target="_blank"
                                                                                                                                                            className="text-blue-500 hover:text-blue-700 underline">privacy
                                        policy</Link>?</div>,
                                }}
                                className="mb-3"
                                buttonProps={getInputProps(fields.agreeToTermsOfServiceAndPrivacyPolicy, {type: 'checkbox'})}
                                errors={fields.agreeToTermsOfServiceAndPrivacyPolicy.errors}
                            />
                        </div>
                        <div className="mb-4 text-center">
                            <input {...getInputProps(fields.redirectTo, {type: 'hidden'})} />
                            <StatusButton
                                className="h-12 w-full rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                                status={isPending ? 'pending' : form.status ?? 'idle'}
                                type="submit"
                                disabled={isPending}
                            >
                                Create
                            </StatusButton>
                        </div>
                    </Form>
                    )}
                </div>
            </div>
        </div>
    )
}

export function ErrorBoundary() {
    return <GeneralErrorBoundary/>
}
